import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Redirect, Link } from 'react-router-dom';
import axios from 'axios';

import useInput from '../../../hooks/useInput';
import Error from '../../common/Error';
import Textarea from '../../common/Textarea';
import Text from '../../common/Input';
import Submit from '../../common/PrimaryButton';
import Select from '../../common/Select';
import MailingAddressChangeValidator from '../../../helpers/validators/endorsements/mailing-address-change.validator';
import findErrorField from '../../../helpers/serverError';
import AlertDispatch from '../../../contexts/alert.dispatch';
import alertActions from '../../../actions/alert.actions';
import File from '../../common/File';
import Normalizer from '../../../helpers/normalizers';
import Format from '../../../helpers/format';

import style from './MailingAddressChange.module.css';

const MailingAddressChange = React.memo(
  ({ clientId, effDate = '', policyIds, setEffDateError }) => {
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({
      streetAddr: '',
      city: '',
      zip: '',
      phone: '',
      file: '',
      state: '',
    });
    const streetAddr = useInput('');
    const city = useInput('');
    const zip = useInput('');
    const phone = useInput('');
    const [states, setStates] = useState([]);
    const [state, setState] = useState('AL');
    const description = useInput('');
    const [file, setFile] = useState();
    const alertDispatch = useContext(AlertDispatch);

    const statesStyle = {
      height: '44px',
      padding: '0 12px',
      fontSize: '16px',
      color: '#636176',
      textOverflow: 'ellipsis',
      boxShadow: 'inset 0 2px 2px 0 rgba(132, 130, 156, 0.2)',
      border: 'solid 1px #b0aeca',
      fontWeight: 'normal',
      marginTop: '10px',
      marginLeft: '0',
    };

    const handlePhone = value => {
      const normalizedPhone = Normalizer.normalizePhoneOrFax(value);
      return Format.formatPhoneOrFax(normalizedPhone);
    };

    const handleSelectState = event => {
      setState(event.target.value);
    };

    const handleSelectFile = nweFile => {
      setFile(nweFile);
    };

    useEffect(() => {
      const handleSuccess = ({ data }) => {
        setStates(
          data.map((item, idx) => {
            return {
              text: item.Name,
              value: item.Value,
              id: idx.toString(),
            };
          }),
        );
      };

      axios
        .get(`${process.env.REACT_APP_BASE_URL}listitems/state`)
        .then(handleSuccess);
    }, []);

    const handleSubmit = event => {
      event.preventDefault();

      const validation = MailingAddressChangeValidator.validate({
        streetAddr: streetAddr.value,
        city: city.value,
        zip: zip.value,
        phone: phone.value,
        effDate,
        file,
      });

      setErrors({
        streetAddr: validation.streetAddr.message,
        city: validation.city.message,
        zip: validation.zip.message,
        phone: validation.phone.message,
        file: validation.file.message,
      });

      setEffDateError(validation.effDate.message);

      const handleSuccess = () => {
        alertDispatch({
          type: alertActions.SUCCESS,
          message:
            'Thank you for your Mailing Address Change request. A member of your dedicated team will respond within 1 business day.',
        });
        setSubmitted(true);
      };

      const handleError = ({ response }) => {
        if (response && response.data && response.data.ValidationErrors) {
          const effDateError = findErrorField(
            response.data.ValidationErrors,
            'EffDate',
          );

          const streetAddrError = findErrorField(
            response.data.ValidationErrors,
            'StreetAddr',
          );

          const cityError = findErrorField(
            response.data.ValidationErrors,
            'City',
          );

          const stateError = findErrorField(
            response.data.ValidationErrors,
            'State',
          );

          const zipError = findErrorField(
            response.data.ValidationErrors,
            'Zip',
          );

          const phoneError = findErrorField(
            response.data.ValidationErrors,
            'Phone',
          );

          const fileError = findErrorField(
            response.data.ValidationErrors,
            'Description',
          );

          setEffDateError(effDateError ? effDateError.Messages[0] : '');

          setErrors({
            streetAddr: streetAddrError ? streetAddrError.Messages[0] : '',
            city: cityError ? cityError.Messages[0] : '',
            zip: zipError ? zipError.Messages[0] : '',
            phone: phoneError ? phoneError.Messages[0] : '',
            file: fileError ? fileError.Messages[0] : '',
            state: stateError ? stateError.Messages[0] : '',
          });
        } else {
          alertDispatch({
            type: alertActions.ERROR,
            message: 'Something went wrong. Please try again',
          });
          setSubmitted(true);
        }
      };

      if (validation.isValid) {
        axios
          .post(
            `${
              process.env.REACT_APP_BASE_URL
            }hub/clients/${clientId}/requests/endorsements/mailing-address-change?policyIds=${policyIds.join(
              '&policyIds=',
            )}`,
            {
              File: file
                ? {
                    Name: file.FileName,
                    Content: file.Content,
                    ContentType: file.ContentType,
                  }
                : null,
              StreetAddr: streetAddr.value,
              City: city.value,
              State: state,
              Zip: zip.value,
              Phone: phone.value,
              Comments: description.value,
              EffDate: effDate,
            },
            {
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
            },
          )
          .then(handleSuccess)
          .catch(handleError);
      }
    };

    return submitted ? (
      <Redirect to={`/clients/${clientId}/endrosements`} />
    ) : (
      <>
        <div className={`form-group ${style.info}`}>
          <div className={style.title}>New Mailing Address:</div>
        </div>
        <div className={`form-group ${style.info}`}>
          <div className={style.label}>Street Address:</div>
          <Textarea name="streetAddr" {...streetAddr} />
          <Error error={errors.streetAddr} />
        </div>
        <div className={`form-group ${style.info}`}>
          <div className={style.label}>City:</div>
          <Text id="city" name="city" {...city} />
          <Error error={errors.city} />
        </div>
        <div className={`form-group ${style.info}`}>
          <div className={style.label}>State:</div>
          <Select
            options={states}
            styles={statesStyle}
            onChange={handleSelectState}
            value={state}
          />
          <Error error={errors.state} />
        </div>
        <div className={`form-group ${style.info}`}>
          <div className={style.label}>Zip Code:</div>
          <Text id="zip" name="zip" {...zip} />
          <Error error={errors.zip} />
        </div>
        <div className={`form-group ${style.info}`}>
          <div className={style.label}>Primary Phone:</div>
          <Text
            id="phone"
            name="phone"
            value={handlePhone(phone.value)}
            onChange={phone.onChange}
          />
          <Error error={errors.phone} />
        </div>
        <div className={`form-group ${style.info}`}>
          <div className={style.label}>Additional Comments:</div>
          <Textarea
            name="description"
            styles={{ minHeight: '90px' }}
            {...description}
          />
        </div>
        <div className={`form-group ${style.info}`}>
          <div className={style.label}>
            A request signed by the Insured is required to submit a change in
            mailing address.
          </div>
        </div>
        <div className={`form-group ${style.info}`}>
          <div className={style.label}>Attachment:</div>
          <File clientId={clientId} handleSelectFile={handleSelectFile} />
          <div>
            <Error error={errors.file} />
          </div>
        </div>
        <div className={style.actions}>
          <Submit
            type="submit"
            text="Submit Request"
            styles={{ width: '200px' }}
            onClick={handleSubmit}
          />
          <Link to={`/clients/${clientId}`} className={style.cancelBtn}>
            Cancel
          </Link>
        </div>
      </>
    );
  },
);

MailingAddressChange.propTypes = {
  clientId: PropTypes.string.isRequired,
  effDate: PropTypes.instanceOf(Date),
  policyIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  setEffDateError: PropTypes.func.isRequired,
};

export default MailingAddressChange;
